import { apiFetch, apiFetchBlob } from '@/lib/api';

export type LeadStatus = 'new' | 'engaged' | 'qualified' | 'follow_up' | 'won' | 'lost';

export interface CapturedItem {
  key: string;
  label: string;
  value: string;
}

export interface Lead {
  id: number;
  instagram_username: string | null;
  instagram_user_id: string | null;
  display_name: string | null;
  phone: string | null;
  email: string | null;
  status: LeadStatus;
  source: string;
  first_seen_at: string;
  last_seen_at: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // F138 — read-only projection of metadata_json; the list endpoint never
  // returns this field (marshmallow omits an absent attribute).
  captured?: CapturedItem[];
  // F152 — reverse lookup of the linked `instagram_contacts` row (dump-only,
  // populated by the backend on the single-lead read path only; the list
  // endpoint never returns this field — see lead_service.get_lead).
  contact_id?: number | null;
}

export interface LeadListResponse {
  items: Lead[];
  total: number;
  page: number;
  page_size: number;
}

export interface LeadListParams {
  status?: LeadStatus;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface LeadCreateRequest {
  instagram_username?: string | null;
  instagram_user_id?: string | null;
  display_name?: string | null;
  phone?: string | null;
  email?: string | null;
  status?: LeadStatus;
  source?: string;
  notes?: string | null;
}

export interface LeadUpdateRequest {
  instagram_username?: string | null;
  display_name?: string | null;
  phone?: string | null;
  email?: string | null;
  status?: LeadStatus;
  notes?: string | null;
}

export interface LeadExportParams {
  status?: LeadStatus;
  search?: string;
}

function buildQuery(params: LeadListParams): string {
  const usp = new URLSearchParams();
  if (params.status) usp.append('status', params.status);
  if (params.search) usp.append('search', params.search);
  if (params.page !== undefined) usp.append('page', String(params.page));
  if (params.page_size !== undefined) usp.append('page_size', String(params.page_size));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

// Module-local helper — shared by exportCsv and any future export methods.
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const leadService = {
  list(params: LeadListParams = {}): Promise<LeadListResponse> {
    return apiFetch<LeadListResponse>(`/leads${buildQuery(params)}`);
  },
  get(id: number): Promise<Lead> {
    return apiFetch<Lead>(`/leads/${id}`);
  },
  create(payload: LeadCreateRequest): Promise<Lead> {
    return apiFetch<Lead>('/leads', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  update(id: number, payload: LeadUpdateRequest): Promise<Lead> {
    return apiFetch<Lead>(`/leads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
  delete(id: number): Promise<void> {
    return apiFetch<void>(`/leads/${id}`, { method: 'DELETE' });
  },
  async exportCsv(params: LeadExportParams = {}): Promise<void> {
    const qs = buildQuery(params);
    const { blob, filename } = await apiFetchBlob(`/leads/export${qs}`);
    triggerDownload(blob, filename);
  },
};
